import i18n from "@/i18n";
import {
  createUser,
  deleteUser,
  fetchActions,
  fetchAthleteStatus,
  fetchStats,
  fetchUserActions,
  fetchUserData,
  prioritizeAthlete,
  scanAthlete,
  sendAccess,
  unscanAthlete,
  updateUser,
} from "@/services/admin.service";
import type { AthleteStatus } from "@/types/admin.types";
import type { Action, User, UserActionData } from "@/types/user.types";
import { errorHandler } from "@/utils/errorHandler";
import { defineStore } from "pinia";
import { useFileFilterStore } from "./fileFilterStore";
import { useGlobalStore } from "./globalStore";

export const useAdminStore = defineStore("adminStore", {
  state: () => ({
    users: [] as User[],
    currentPage: 1,
    minPage: 1,
    total: 0,
    maxPage: 0,
    limit: 10,
    roles: [] as string[],
    inputValue: "",
    searchUser: "",
    searchAthleteStatus: "",
    email: "",
    id: "",
    selectedUsers: [] as string[],
    selectedUserId: "",
    usersByRole: {} as Record<string, User[]>,
    athletesStatus: [] as AthleteStatus[],
    userActions: {} as UserActionData,
    actions: [] as Action[],
    ftpCreation: false,
    actionTypes: [
      "ENVELOPE_ATHLETE_LINK",
      "ENVELOPE_ATHLETE_UNLINK",
      "ENVELOPE_START_VALIDATION",
      "ENVELOPE_FILE_UPLOAD",
      "ENVELOPE_ATHLETE_SWITCH",
      "ENVELOPE_REGENERATE",
      "ENVELOPE_REMOVE_FROM_PRODUCTION",
      "ENVELOPE_FILES_SWAP",
      "ENVELOPE_BACKUP_PROCESS",
      "ENVELOPE_LEAVE_VALIDATION",
      "ENVELOPE_SEND_TO_VALIDATION",
      "ENVELOPE_MARK_AS_DUPLICATE",
      "ENVELOPE_MARK_MANUAL_INTERVENTION",

      "FILE_MOVE",
      "FILE_MARK_DISCARDED",
      "FILE_MARK_NOBODY_RECOGNIZED",
      "FILE_SWAP_DESCRIPTIONS",
      "FILE_RESEND_RECOGNITION",
      "FILE_NEW_UPLOAD",
      "FILE_RECOGNITION_RESPONSE",
      "FILE_BACKGROUND_REMOVAL",
      "FILE_CROP_IMAGE",

      "QUALITY_CONTROL_START",
      "QUALITY_CONTROL_VALIDATE",
      "ENVELOPE_MARK_UNPROCESSABLE",

      "ATHLETE_BULK_UPLOAD",
      "ATHLETE_UPDATE",
      "ATHLETE_TOGGLE_PRIORITY",

      "USER_CREATE_OWNER",
      "USER_CREATE",
      "USER_UPDATE",
      "USER_DELETE",
      "USER_FIRST_CONNECTION",
      "USER_ASSIGN_KIT",
      "USER_UNASSIGN_KIT",
      "USER_ASSIGN_SITE",

      "FILE_ERROR_CREATE",
      "FILE_ERROR_GET_REPORT",
      "FILE_ERROR_FIX",

      "CHAT_CREATE",
      "CHAT_UPDATE",
      "CHAT_DELETE",
      "CHAT_USER_ADD",
      "CHAT_USER_REMOVE",

      "QR_CODE_SCAN",

      "SITE_CREATE",
      "SITE_CREATE_BULK",
      "SITE_UPDATE",
      "KIT_CREATE",
      "KIT_UPDATE",
      "CLUSTER_CREATE",
      "CLUSTER_UPDATE",
    ],
  }),

  getters: {
    hasSelectedUsers(state): boolean {
      return this.selectedUsers.length > 0;
    },
  },

  actions: {
    /// CALL API ///

    // Get Statistics of application by filter
    async fetchStats() {
      const fileFilterStore = useFileFilterStore();
      try {
        const res = await fetchStats(
          fileFilterStore.selectedDateFilter,
          fileFilterStore.selectedClusterFilter,
          fileFilterStore.selectedSiteFilter,
          fileFilterStore.selectedDisciplineFilter,
          fileFilterStore.selectedCountryFilter,
          fileFilterStore.selectedFileProcessFilter
        );
        if (!res) {
          console.log("no user data found");
          return;
        }
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      }
    },

    // Get List of athletes status with filter
    async fetchAthleteStatus() {
      const globalStore = useGlobalStore();
      const fileFilterStore = useFileFilterStore();
      let sortOrder = fileFilterStore.selectedOrderTimeFilter ?? "DESC";
      let dateFilter = fileFilterStore.selectedDateFilter ?? "NONE";
      let status = fileFilterStore.selectedStatusFilter ?? "";
      let country = fileFilterStore.selectedCountryFilter ?? "";
      let discipline = fileFilterStore.selectedDisciplineFilter ?? "";
      try {
        globalStore.showSkeletonLoading = true;
        this.athletesStatus = [];
        const res = await fetchAthleteStatus(
          this.currentPage,
          this.limit,
          this.searchAthleteStatus,
          sortOrder,
          dateFilter,
          status,
          country,
          discipline
        );
        if (!res) {
          console.log("no user data found");
          return;
        }
        this.athletesStatus = res.items;
        this.total = res.total;
        this.maxPage = Math.ceil(res.total / this.limit);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        globalStore.showSkeletonLoading = false;
      }
    },

    // Get List of users with filter
    async fetchUserData(role?: string) {
      const globalStore = useGlobalStore();
      globalStore.showSkeletonLoading = true;
      try {
        const res = await fetchUserData(
          this.currentPage,
          this.limit,
          this.searchUser,
          role || ""
        );
        if (!res) {
          console.log("no user data found");
          return;
        }

        this.users = res.items;
        this.total = res.total;
        this.maxPage = Math.ceil(res.total / this.limit);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        globalStore.showSkeletonLoading = false;
      }
    },

    // Get List of user action
    async fetchUserActions(id: string) {
      try {
        const res = await fetchUserActions(
          id,
          this.currentPage,
          this.limit,
          this.searchUser
        );
        if (!res) {
          console.log("no user data found");
          return;
        }
        this.userActions = res;
        this.total = res.total;
        this.maxPage = Math.ceil(res.total / this.limit);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      }
    },

    // Get List of last actions
    async fetchActions() {
      const fileFilterStore = useFileFilterStore();
      let sortOrder = fileFilterStore.selectedOrderTimeFilter ?? "DESC";
      let dateFilter = fileFilterStore.selectedDateFilter ?? "NONE";
      try {
        const res = await fetchActions(
          this.currentPage,
          this.limit,
          sortOrder,
          dateFilter,
          fileFilterStore.selectedRoleFilter,
          fileFilterStore.selectedActionTypeFilter,
          this.searchUser
        );
        if (!res) {
          console.log("no user data found");
          return;
        }
        this.actions = res.items;
        this.total = res.total;
        this.maxPage = Math.ceil(res.total / this.limit);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      }
    },

    // Create new user
    async postUser() {
      const emailExist = this.users.some(
        (user) => user.email === this.email.trim()
      );
      if (emailExist) {
        throw new Error("L'adresse e-mail existe déjà.");
      }

      try {
        await createUser(this.email, this.roles, this.ftpCreation);
        this.roles = [];
        this.email = "";
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      }
    },

    // Delete user
    async deleteUser(id: string) {
      try {
        await deleteUser(id);
        this.id = "";
        this.users = this.users.filter((user) => user.id !== id);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      }
    },

    // Delete multiple user
    async deleteAllselectedUser() {
      try {
        await Promise.all(this.selectedUsers.map((id) => this.deleteUser(id)));
        this.selectedUsers = [];
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      }
    },

    // Update user
    async updateUser() {
      try {
        const selectedUser = this.users.find(
          (u) => u.id === this.selectedUserId
        );
        let ftpValueToSend: boolean | undefined = undefined;

        if (selectedUser && this.ftpCreation !== selectedUser.ftpEnabled) {
          ftpValueToSend = this.ftpCreation;
        }

        await updateUser(this.selectedUserId, this.roles, ftpValueToSend);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      }
    },

    // Update user
    async scanAthlete(athleteId: string, disciplineCode?: string) {
      try {
        await scanAthlete(athleteId, disciplineCode);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      }
    },

        async unscanAthlete(athleteId: string, disciplineCode?: string) {
      try {
        await unscanAthlete(athleteId, disciplineCode);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      }
    },

    // Resend access to user
    async resendAccess(id: string) {
      try {
        await sendAccess(id);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      }
    },

    //Prioritize user
    async prioritizeAthlete(id: string, disciplineCode: string) {
      const globalStore = useGlobalStore();
      try {
        globalStore.showLoader = true;
        await prioritizeAthlete(id, disciplineCode);
        await this.fetchAthleteStatus();
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        globalStore.showLoader = false;
      }
    },

    /// ADMIN VIEW LOGIC ///

    selectMutipleUsers(id: string) {
      const idIndex = this.selectedUsers.indexOf(id);

      if (idIndex !== -1) {
        this.selectedUsers.splice(idIndex, 1);
      } else {
        this.selectedUsers.push(id);
      }
    },

    isSelectedUser(id: string) {
      return this.selectedUsers.includes(id);
    },

    increment() {
      if (this.currentPage < this.maxPage) {
        this.currentPage++;
        this.fetchUserData();
      }
    },

    decrement() {
      if (this.currentPage > this.minPage) {
        this.currentPage--;
        this.fetchUserData();
      }
    },

    stringToColor(str: string): string {
      // Liste de couleurs prédéfinies
      const predefinedColors = [
        "#EC71D5",
        "#717BEC",
        "#12CE9F",
        "#099B66",
        "#60A330",
        "#02ABD6",
        "#F45E60",
        "#EC718A",
        "#9747FF",
        "#2E82DD",
      ];

      // Génère une couleur à partir du string (comme avant)
      let hash = 0;
      for (let i = 0; i < str?.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      const r = (hash >> 16) & 0xff;
      const g = (hash >> 8) & 0xff;
      const b = hash & 0xff;

      // Fonction pour calculer la distance entre deux couleurs RGB
      function colorDistance(c1: number[], c2: number[]): number {
        return Math.sqrt(
          Math.pow(c1[0] - c2[0], 2) +
            Math.pow(c1[1] - c2[1], 2) +
            Math.pow(c1[2] - c2[2], 2)
        );
      }

      // Convertit hex en RGB
      function hexToRgb(hex: string): number[] {
        hex = hex.replace("#", "");
        return [
          parseInt(hex.substring(0, 2), 16),
          parseInt(hex.substring(2, 4), 16),
          parseInt(hex.substring(4, 6), 16),
        ];
      }

      const generatedColor = [r, g, b];

      // Trouve la couleur prédéfinie la plus proche
      let closestColor = predefinedColors[0];
      let minDistance = colorDistance(
        generatedColor,
        hexToRgb(predefinedColors[0])
      );

      for (const color of predefinedColors) {
        const dist = colorDistance(generatedColor, hexToRgb(color));
        if (dist < minDistance) {
          minDistance = dist;
          closestColor = color;
        }
      }

      return closestColor;
    },
    reset() {
      this.searchAthleteStatus = "";
      this.currentPage = 1;
      this.searchUser = "";
      this.total = 0;
      this.maxPage = 0;
    },
    formatDate(dateStr: string) {
      const locale = i18n.global.locale ?? "fr-FR";
      const date = new Date(dateStr);

      const dayName = date.toLocaleDateString(locale, { weekday: "long" });
      const dayNum = date.getDate();
      const month = date
        .toLocaleDateString(locale, { month: "short" })
        .replace(".", "");
      const time = date.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
      });

      const formattedTime =
        locale.startsWith("fr") || locale.startsWith("es")
          ? time.replace(":", "h")
          : time;

      const atText = i18n.global.t("admin.at");
      return `${this.capitalize(
        dayName
      )} ${dayNum} ${month} ${atText} ${formattedTime}`;
    },

    capitalize(str: string) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
  },
});
